import { Component, ChangeDetectorRef, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SAUModalService } from '@some-angular-utils/modal';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { ProductFormComponent } from '../product-form/product-form.component';
import { CustomDeleteComponent } from '@/components/custom-delete/custom-delete.component';
import { AddButtonComponent } from '@/components/add-button/add-button.component';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { LoadingComponent } from '@/components/loading/loading.component';
import { FormsModule } from '@angular/forms';

/** Tailwind color combos cycled per category, purely decorative (products/categories have no color field). */
const CATEGORY_PALETTE = [
  'bg-emerald-50 border-emerald-200 text-emerald-700',
  'bg-blue-50 border-blue-200 text-blue-700',
  'bg-amber-50 border-amber-200 text-amber-700',
  'bg-rose-50 border-rose-200 text-rose-700',
  'bg-violet-50 border-violet-200 text-violet-700',
  'bg-cyan-50 border-cyan-200 text-cyan-700',
  'bg-lime-50 border-lime-200 text-lime-700',
  'bg-orange-50 border-orange-200 text-orange-700',
];

interface CategoryGroup {
  id: number | null;
  name: string;
  classes: string;
  products: any[];
}

/**
 * Alternate, card-grid view of the same `products` data as product-list.page.ts, grouped by
 * category and sized for quick touch selection - a restaurant-focused way to browse the
 * catalog, not a separate product concept. Editing reuses ProductFormComponent with
 * `restaurantView` on for a shorter form. See CLAUDE.md products section.
 */
@Component({
  imports: [
    RouterModule,
    FormsModule,
    AddButtonComponent,
    CentToEurDisplayDirective,
    LoadingComponent,
  ],
  templateUrl: './product-restaurant-view.page.html',
})
export class ProductRestaurantViewPage implements OnInit {

  public loading = signal(false);
  public products: any[] = [];
  public search = '';
  public selectedCategoryId: number | null | 'all' = 'all';

  private categoryColors = new Map<number | null, string>();

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private sauModalService: SAUModalService,
  ) { }

  ngOnInit(): void {
    this.getProducts();
  }

  getProducts() {
    this.loading.set(true);
    this.apiService.getPetition(Urls.urls.products, '?limit=1000').subscribe({
      next: (result: any) => {
        this.products = result.list || [];
        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  get categories(): { id: number | null; name: string; classes: string }[] {
    const seen = new Map<number | null, { id: number | null; name: string }>();

    this.products.forEach(p => {
      const id = p.category?.id ?? null;
      const name = p.category?.name ?? 'Sin familia';
      if (!seen.has(id)) seen.set(id, { id, name });
    });

    return [...seen.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => ({ ...c, classes: this.getCategoryColor(c.id) }));
  }

  getCategoryColor(categoryId: number | null): string {
    if (!this.categoryColors.has(categoryId)) {
      this.categoryColors.set(categoryId, CATEGORY_PALETTE[this.categoryColors.size % CATEGORY_PALETTE.length]);
    }
    return this.categoryColors.get(categoryId)!;
  }

  selectCategory(id: number | null | 'all') {
    this.selectedCategoryId = id;
  }

  get groups(): CategoryGroup[] {
    const q = this.search.trim().toLowerCase();

    const filtered = this.products.filter(p => {
      const matchesSearch = !q || p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q);
      const matchesCategory = this.selectedCategoryId === 'all' || (p.category?.id ?? null) === this.selectedCategoryId;
      return matchesSearch && matchesCategory;
    });

    const byCategory = new Map<number | null, CategoryGroup>();

    filtered.forEach(p => {
      const id = p.category?.id ?? null;
      const name = p.category?.name ?? 'Sin familia';

      if (!byCategory.has(id)) {
        byCategory.set(id, { id, name, classes: this.getCategoryColor(id), products: [] });
      }
      byCategory.get(id)!.products.push(p);
    });

    return [...byCategory.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(g => ({ ...g, products: g.products.sort((a, b) => a.name.localeCompare(b.name)) }));
  }

  openEditForm(item: { id: number } = { id: 0 }) {
    const modalRef = this.sauModalService.open(ProductFormComponent, {});
    modalRef.componentInstance.id = item.id;
    modalRef.componentInstance.restaurantView = true;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getProducts();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openDeleteForm(item: any, event: Event) {
    event.stopPropagation();

    const modalRef = this.sauModalService.open(CustomDeleteComponent, {});
    modalRef.componentInstance.id = item.id;
    modalRef.componentInstance.url = Urls.urls.products;
    modalRef.componentInstance.item = 'producto';

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getProducts();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

}
